import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import { getCalendarAvailability } from '~/modules-rocco/booking/api/get-calendar-availability.server';
import type { CalendarAvailabilityData } from '~/modules-rocco/booking/api/get-calendar-availability.server';

export const BOOKING_AVAILABILITY_API_ROUTE = '/remix-api/booking/availability';

interface BookingAvailabilityError {
    code: 'notFound' | 'loadFailed' | 'sessionDisabled' | 'noConsultant';
    message: string;
}

export interface BookingAvailabilityApiResponse {
    availability?: CalendarAvailabilityData;
    error?: BookingAvailabilityError;
}

const QUERY_SCHEMA = z.object({
    sessionId: z.coerce.number().int().positive(),
    timezone: z.string().min(1).optional(),
});

/**
 * Client-side data source for the in-page booking modal. Wraps the same
 * {@link getCalendarAvailability} the /booking page loader uses and applies the
 * identical active + connected-member filtering, so the modal renders exactly
 * what the standalone booking page would for a given session.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const parsed = QUERY_SCHEMA.safeParse({
        sessionId: url.searchParams.get('sessionId'),
        timezone: url.searchParams.get('timezone') ?? undefined,
    });

    if (!parsed.success) {
        return routerData<BookingAvailabilityApiResponse>(
            {
                error: {
                    code: 'notFound',
                    message: 'A valid booking session id is required.',
                },
            },
            { status: 400 },
        );
    }

    const { sessionId, timezone } = parsed.data;
    const result = await getCalendarAvailability(sessionId, context, timezone);

    if (!result.success) {
        return routerData<BookingAvailabilityApiResponse>(
            { error: result.error },
            { status: result.error.code === 'notFound' ? 404 : 400 },
        );
    }

    const availability = result.data;

    if (!availability.isActive) {
        return routerData<BookingAvailabilityApiResponse>(
            {
                error: {
                    code: 'sessionDisabled',
                    message: 'This booking session is not currently available.',
                },
            },
            { status: 403 },
        );
    }

    // Drop team members without a usable calendar connection (mirrors the page loader).
    const connectedTeamMembers =
        availability.teamMembers?.filter((member) => {
            const connections = member.CalendarConnection || [];
            return connections.some((c) => c.email && c.provider);
        }) || [];

    const hasConnectedOneOnOneMember =
        availability.meetingType === 'one-on-one' &&
        availability.oneOnOneTeamMember?.CalendarConnection?.some(
            (c) => c.email && c.provider,
        );

    const hasConnectedMembers =
        availability.meetingType === 'one-on-one'
            ? hasConnectedOneOnOneMember
            : connectedTeamMembers.length > 0;

    if (!hasConnectedMembers) {
        return routerData<BookingAvailabilityApiResponse>(
            {
                error: {
                    code: 'noConsultant',
                    message:
                        'No team members are currently available for booking.',
                },
            },
            { status: 409 },
        );
    }

    if (availability.meetingType !== 'one-on-one') {
        availability.teamMembers = connectedTeamMembers;
    }

    return routerData<BookingAvailabilityApiResponse>({ availability });
};
